import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeDollarSign,
  Clock,
  Languages,
  Mail,
  MapPin,
} from "lucide-react";
import { Country, getCountriesList, getLanguagesList } from "../../const/countryList";
import { useAuth } from "../../context/AuthContext";
import { decodeTokenService } from "../../services/decodeTokenService";
import { getUserSettings } from "../../services/settings/userSettings";
import { handleApi } from "../../utils/handleApi";
import { Language } from "../../utils/dictionary";
import { getCurrencySymbol, getTimezoneUtcOffset } from "../../utils/formatters";

let cachedUserSettings: UserSettingsResponse | null = null;

type UserSettingsResponse = {
  country?: string;
  currency?: string;
  language?: string;
  time_zone?: string;
  display_name?: string;
};

const copy = {
  account: {
    [Language.SPANISH]: "Cuenta",
    [Language.ENGLISH]: "Account",
    [Language.PORTUGUESE]: "Conta",
  },
  personalInfo: {
    [Language.SPANISH]: "Perfil",
    [Language.ENGLISH]: "User information",
    [Language.PORTUGUESE]: "Informações do usuário",
  },
  sellingSetup: {
    [Language.SPANISH]: "Configuración de venta",
    [Language.ENGLISH]: "Selling setup",
    [Language.PORTUGUESE]: "Configuração de venda",
  },
  country: {
    [Language.SPANISH]: "País de venta",
    [Language.ENGLISH]: "Selling country",
    [Language.PORTUGUESE]: "País de venda",
  },
  currency: {
    [Language.SPANISH]: "Moneda",
    [Language.ENGLISH]: "Currency",
    [Language.PORTUGUESE]: "Moeda",
  },
  language: {
    [Language.SPANISH]: "Idioma de la cuenta",
    [Language.ENGLISH]: "Account language",
    [Language.PORTUGUESE]: "Idioma da conta",
  },
  timezone: {
    [Language.SPANISH]: "Zona horaria",
    [Language.ENGLISH]: "Timezone",
    [Language.PORTUGUESE]: "Fuso horário",
  },
  locked: {
    [Language.SPANISH]: "No puedes cambiar la moneda ni el país de tu cuenta. Si necesitas ajustarlos, soporte debe revisarlo.",
    [Language.ENGLISH]: "You cannot change your account currency or country. If you need to adjust them, support must review it.",
    [Language.PORTUGUESE]: "Você não pode alterar a moeda nem o país da sua conta. Se precisar ajustá-los, o suporte deve revisar.",
  },
  lockedBadge: {
    [Language.SPANISH]: "Fija",
    [Language.ENGLISH]: "Fixed",
    [Language.PORTUGUESE]: "Fixa",
  },
};

const DetailRow = ({
  icon,
  label,
  value,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  badge?: string;
}) => (
  <div className="grid min-h-14 grid-cols-1 gap-2 border-b border-gray-100 py-3 last:border-b-0 sm:grid-cols-[240px_minmax(0,1fr)] sm:items-center">
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-gray-800 ring-1 ring-gray-200">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium tracking-normal text-gray-400">{label}</p>
      </div>
    </div>
    <div className="flex min-w-0 items-center gap-2 sm:justify-start">
      <p className="truncate text-sm font-semibold text-gray-950">{value || "-"}</p>
      {badge && (
        <span className="shrink-0 rounded-full bg-[#eef3ff] px-2.5 py-1 text-[11px] font-semibold text-[#26317f]">
          {badge}
        </span>
      )}
    </div>
  </div>
);

export function AccountSettingsPanel() {
  const { language, country, timezone } = useAuth();
  const [settings, setSettings] = useState<UserSettingsResponse | null>(cachedUserSettings);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const { res, error } = await handleApi(getUserSettings(), false);
      if (!mounted || error) return;
      cachedUserSettings = (res as UserSettingsResponse) ?? null;
      setSettings(cachedUserSettings);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const countries = useMemo(() => getCountriesList(language), [language]);
  const languages = useMemo(() => getLanguagesList(language), [language]);
  const countryCode = (settings?.country || country) as Country;
  const currentCountry = countries.find((item) => item.country === countryCode);
  const languageCode = (settings?.language || language) as Language;
  const currentLanguage = languages.find((item) => item.code === languageCode);
  const currency = settings?.currency || currentCountry?.currency || "USD";
  const accountTimezone = settings?.time_zone || timezone;
  const email = decodeTokenService.getEmailFromToken() || "";

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
          {copy.account[language]}
        </h1>
      </header>

      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-xs font-semibold tracking-normal text-gray-400">
            {copy.personalInfo[language]}
          </h2>
          <DetailRow
            icon={<Mail className="h-5 w-5" />}
            label="Email"
            value={email}
          />
          <DetailRow
            icon={<Languages className="h-5 w-5" />}
            label={copy.language[language]}
            value={currentLanguage?.name || languageCode}
          />
          <DetailRow
            icon={<Clock className="h-5 w-5" />}
            label={copy.timezone[language]}
            value={`${accountTimezone} ${getTimezoneUtcOffset(accountTimezone)}`}
          />
        </div>

        <div>
          <h2 className="mb-2 text-xs font-semibold tracking-normal text-gray-400">
            {copy.sellingSetup[language]}
          </h2>
          <DetailRow
            icon={<MapPin className="h-5 w-5" />}
            label={copy.country[language]}
            value={currentCountry?.name || countryCode}
          />
          <DetailRow
            icon={<BadgeDollarSign className="h-5 w-5" />}
            label={copy.currency[language]}
            value={`${currency} ${getCurrencySymbol(countryCode)}`}
            badge={copy.lockedBadge[language]}
          />
        </div>
      </section>

      <div className="flex max-w-[620px] gap-2.5 border-t border-gray-100 px-1 py-3 text-gray-500">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
        <p className="min-w-0 text-xs leading-5">{copy.locked[language]}</p>
      </div>
    </div>
  );
}
